/**
 * Referral (09) — the prescriber-attribution surface FeatureModule. CLOUD-only (D-28): on a LOCAL
 * install the /referral/* routes are not mounted, so every read 404s; each screen detects that via
 * `isModuleUnmounted` and renders <UnavailableState/> ("indisponible sur cette installation") instead of
 * an error — the module degrades cleanly rather than appearing broken. (The nav group still appears for a
 * cloud-capable role; the screens self-explain on a local box.)
 *
 * Three screens under /referral/*:
 *   /referral/liens    — the lab↔médecin link table (invite → approve → revoke)
 *   /referral/codes    — CodeReferral creation + the referral-analytics cards (KPI + Recharts + candidate)
 *   /referral/retours  — the prescriber-return tracking list (per médecin)
 *
 * Gating model. The three screens need DIFFERENT capabilities (analytics.read vs lien.manage vs
 * code.manage vs retour.read), and the router applies ONE module-level gate per route. So — like the
 * inventory + finance modules — we keep the MODULE gate empty (any authenticated user reaches the shell)
 * and gate two ways:
 *   1. each NAV item carries its own requiredCapabilities (the side-nav shows only what you may open);
 *   2. each SCREEN renders its own ForbiddenState (via QueryBoundary) when its read capability is missing,
 *      and every mutating action is gated by its exact server capability via useCan.
 * The server re-checks every request — useCan only hides/disables UI (never the trust boundary).
 *
 * Capabilities used (exact server keys):
 *   referral.lien.manage · referral.code.manage · referral.remise.set · referral.retour.read ·
 *   referral.analytics.read
 */
import type { FeatureModule } from "@/app";
import { REF_CAPS } from "./caps";
import { registerReferralMessages } from "./i18n";
import { LiensPage } from "./screens/LiensPage";
import { CodesPage } from "./screens/CodesPage";
import { RetoursPage } from "./screens/RetoursPage";

const referralModule: FeatureModule = {
  id: "referral",
  title: "Médecins prescripteurs",
  // Empty module gate (see header): per-screen ForbiddenState + per-nav-item caps do the gating.
  requiredCapabilities: [],
  routes: [
    { path: "/referral/liens", element: <LiensPage /> },
    { path: "/referral/codes", element: <CodesPage /> },
    { path: "/referral/retours", element: <RetoursPage /> },
  ],
  nav: [
    {
      id: "referral-liens",
      path: "/referral/liens",
      label: "nav.referral.liens",
      icon: "⇄",
      order: 80,
      section: "nav.section.referral",
      requiredCapabilities: [REF_CAPS.analyticsRead],
    },
    {
      id: "referral-codes",
      path: "/referral/codes",
      label: "nav.referral.codes",
      icon: "▦",
      order: 81,
      section: "nav.section.referral",
      requiredCapabilities: [REF_CAPS.analyticsRead],
    },
    {
      id: "referral-retours",
      path: "/referral/retours",
      label: "nav.referral.retours",
      icon: "⤺",
      order: 82,
      section: "nav.section.referral",
      requiredCapabilities: [REF_CAPS.retourRead],
    },
  ],
  onRegister() {
    registerReferralMessages();
  },
};

export default referralModule;
